/**
 * Custom React Flow node components for the Processes canvas.
 *
 * Layout: left → right. All non-decoration nodes have target=Left and source=Right.
 * Decision nodes have one target (Left) and three sources (Right=default, Top=yes, Bottom=no).
 *
 * Node types:
 *   - procedure : rounded rectangle (action box)
 *   - decision  : diamond
 *   - subprocess: doubled rectangle (drillable into another process)
 *   - start/end : pill (oval) — start has source only, end has target only
 *   - wait/event: rectangle with badge
 *   - lane      : full-width swim lane background with seat title (read-only)
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';
import { ListChecks, Diamond, Layers, Play, Square, Clock, Zap, Users } from 'lucide-react';

interface ProcessNodeData extends Record<string, unknown> {
  title: string;
  description?: string;
  kpiCount?: number;
  ownerSeats?: Array<{ seat_title: string; role: string }>;
}

function NodeChrome({
  icon, title, description, kpiCount, ownerSeats, accent,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  kpiCount?: number;
  ownerSeats?: Array<{ seat_title: string; role: string }>;
  accent: string;
}) {
  return (
    <div className="flex flex-col gap-1 px-3 py-2 min-w-[180px] max-w-[260px]">
      <div className="flex items-center gap-1.5">
        <span className={`shrink-0 ${accent}`}>{icon}</span>
        <span className="font-medium text-sm leading-tight truncate">{title}</span>
      </div>
      {description && (
        <div className="text-xs text-muted-foreground line-clamp-2">{description}</div>
      )}
      {(kpiCount || ownerSeats?.length) ? (
        <div className="flex flex-wrap gap-1 mt-1">
          {kpiCount ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
              {kpiCount} KPI{kpiCount > 1 ? 's' : ''}
            </span>
          ) : null}
          {(ownerSeats || []).slice(0, 2).map((s, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 truncate max-w-[120px]">
              {s.seat_title}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProcedureNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="rounded-md border-2 border-slate-300 bg-white shadow-sm hover:border-slate-500 transition-colors">
      <Handle type="target" position={Position.Left} />
      <NodeChrome icon={<ListChecks className="h-4 w-4" />} title={d.title} description={d.description} kpiCount={d.kpiCount} ownerSeats={d.ownerSeats} accent="text-slate-600" />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export function DecisionNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="relative" style={{ width: 200, height: 140 }}>
      <div
        className="absolute inset-0 bg-amber-50 border-2 border-amber-400 hover:border-amber-600 transition-colors"
        style={{ transform: 'rotate(45deg)', transformOrigin: 'center' }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center pointer-events-none">
        <div className="flex items-center gap-1.5 mb-1">
          <Diamond className="h-3.5 w-3.5 text-amber-600" />
          <span className="font-medium text-xs leading-tight">{d.title}</span>
        </div>
      </div>
      <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
      <Handle type="source" position={Position.Right} id="default" style={{ top: '50%' }} />
      <Handle type="source" position={Position.Top} id="yes" style={{ left: '50%' }} />
      <Handle type="source" position={Position.Bottom} id="no" style={{ left: '50%' }} />
    </div>
  );
}

export function SubprocessNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="rounded-md border-2 border-indigo-400 bg-indigo-50 shadow-md hover:border-indigo-600 transition-colors relative">
      <div className="absolute inset-1 border border-indigo-300 rounded-sm pointer-events-none" />
      <Handle type="target" position={Position.Left} />
      <NodeChrome icon={<Layers className="h-4 w-4" />} title={d.title} description={d.description} kpiCount={d.kpiCount} ownerSeats={d.ownerSeats} accent="text-indigo-600" />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export function StartNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="rounded-full border-2 border-emerald-500 bg-emerald-50 shadow-sm">
      <NodeChrome icon={<Play className="h-3.5 w-3.5" />} title={d.title} accent="text-emerald-700" />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export function EndNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="rounded-full border-2 border-rose-500 bg-rose-50 shadow-sm">
      <Handle type="target" position={Position.Left} />
      <NodeChrome icon={<Square className="h-3.5 w-3.5" />} title={d.title} accent="text-rose-700" />
    </div>
  );
}

export function WaitNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="rounded-md border-2 border-slate-300 bg-slate-50 shadow-sm border-dashed">
      <Handle type="target" position={Position.Left} />
      <NodeChrome icon={<Clock className="h-4 w-4" />} title={d.title} description={d.description} accent="text-slate-500" />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export function EventNode({ data }: NodeProps) {
  const d = data as ProcessNodeData;
  return (
    <div className="rounded-md border-2 border-purple-400 bg-purple-50 shadow-sm">
      <Handle type="target" position={Position.Left} />
      <NodeChrome icon={<Zap className="h-4 w-4" />} title={d.title} description={d.description} accent="text-purple-600" />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

interface LaneData extends Record<string, unknown> {
  title: string;
  width: number;
  height: number;
}

export function LaneNode({ data }: NodeProps) {
  const d = data as LaneData;
  return (
    <div
      className="bg-blue-50/40 border border-dashed border-blue-200 rounded-md flex items-stretch"
      style={{ width: d.width, height: d.height }}
    >
      <div className="bg-blue-100/60 border-r border-dashed border-blue-200 px-3 py-2 flex items-start gap-1.5 rounded-l-md min-w-[140px]">
        <Users className="h-3.5 w-3.5 text-blue-700 mt-0.5 shrink-0" />
        <span className="text-xs font-medium text-blue-900 leading-tight">{d.title}</span>
      </div>
    </div>
  );
}

export const nodeTypes = {
  procedure: ProcedureNode,
  decision: DecisionNode,
  subprocess: SubprocessNode,
  start: StartNode,
  end: EndNode,
  wait: WaitNode,
  event: EventNode,
  lane: LaneNode,
};
